import {
    ScoredCreditCardProduct,
    CreditCardProductCategory,
} from '@ilg-totallymoney/types'

export enum ApplicationURLTriggerLocation {
    Deeplink = 'deeplink',
    Header = 'header',
    Row = 'row',
    SponsoredRow = 'sponsored-row',
}

export enum OfferType {
    MarketWide = 'Market-wide',
    TMExclusive = 'TM exclusive',
}

export type { CreditCardProductCategory }

export interface ScoredCreditCardProductOfferDetail {
    annualFee: number
    balanceTransferAER?: number
    balanceTransferLimit?: number
    cashAdvanceAER?: number
    cashbackValue?: number
    durationOfBalanceRate?: string
    durationOfBalanceRateMonths?: number
    durationOfPurchaseRate?: string
    durationOfPurchaseRateMonths?: number
    higherMinPayment?: number
    internationalCharges?: number
    introBalanceTransferFee: number
    introBalanceTransferRateAER?: number
    introPurchaseRateAER?: number
    introPurchaseRatePerMonth?: number
    minAge?: number
    minIncome?: number
    minPayment?: number
    minPayment1?: number
    provider: string
    purchaseAER: number
    purchaseRatePerMonth?: number
    representativeAPR: number
    representativeExampleCreditLimit?: number
    rewardsValue?: number
    useGuaranteedAPR?: boolean
}

export interface ScoredCreditCardProductCategory {
    displayOrder: number
    featured: boolean
    name: string
}

export enum ScoredCreditCardProductScoreStatus {
    NotRated = 70,
    Scored = 80,
    PreApprovedProduct = 90,
    PreApprovedRate = 100,
}

export interface ScoredCreditCardProductsCategory {
    creditCards: ScoredCreditCardProduct[]
    displayName: string
    id: CreditCardProductCategory
    templateId: CreditCardProductCategory
    totalCreditCards: number
}

export interface CreditCardAlternativeProduct {
    apr: number
    clickId?: string
    cons: string[]
    description: string
    displayOrder: number
    header: string
    imageUrl: string
    imageUrlLarge: string
    imageUrlMedium: string
    imageUrlSmall: string
    name: string
    productCode: string
    pros: string[]
    representativeExample: string
}

export interface CreditCardAlternativeProductsCategory {
    alternativeProductCategoryId: string
    alternativeProducts: CreditCardAlternativeProduct[]
    alternativeProductsCount: number
    description: string
    displayName: string
    id: string
}

export interface FetchScoredCreditCardProductsResponse {
    allActiveCards: ActiveCreditCard[]
    experiments?: ScoredExperiment[]
    scoredCategories: ScoredCreditCardProductsCategory[]
    thirdPartyReference: string
}

export interface FetchCreditCardAlternativeProductsResponse {
    categories: CreditCardAlternativeProductsCategory[]
}

export enum ModalType {
    AnnualInterestRatesModal = 'AnnualInterestRatesModal',
    BalanceTransferOfferModal = 'BalanceTransferOfferModal',
    BestMatchModal = 'BestMatchModal',
    DevToolClickoutModal = 'DevToolClickoutModal',
    GuaranteedAprModal = 'GuaranteedAprModal',
    GuaranteedCreditLimitModal = 'GuaranteedCreditLimitModal',
    OtherKeyFactsModal = 'OtherKeyFactsModal',
    PurchaseOfferModal = 'PurchaseOfferModal',
    RepresentativeExampleModal = 'RepresentativeExampleModal',
}

export interface ModalState {
    activeCard: ScoredCreditCardProduct | null
    activeModal: ModalType | null
    creditCardApplicationUrl: string | null
}

export const defaultModalState: ModalState = {
    activeCard: null,
    activeModal: null,
    creditCardApplicationUrl: null,
}

export interface CreditCardsTableTestCase {
    data: FetchScoredCreditCardProductsResponse | { scoredCategories: null }
    highlightCardPath?: string
    showLoading?: boolean
    showUninitialized?: boolean
}

export interface AlternativeProductsTestCase {
    data: FetchCreditCardAlternativeProductsResponse | null
    showError?: boolean
    showLoading?: boolean
    showUninitialized?: boolean
}

export interface CreditCardDetailsTestCase {
    cardId: string
    category: CreditCardProductCategory
    data: FetchScoredCreditCardProductsResponse | { scoredCategories: null }
    showLoading?: boolean
}

export type CreditCardsTestCase = {
    alternativeProducts?: AlternativeProductsTestCase
    creditCard?: CreditCardDetailsTestCase
    creditCards?: CreditCardsTableTestCase
}

export interface ActiveCreditCard {
    imageUrlLarge: string
    name: string
    productCode: string
}

export interface SetMinimumGuaranteedCreditLimitRequest {
    customerId: string
    minimumGuaranteedCreditLimit: number
}

export interface SetMinimumGuaranteedCreditLimitResponse {
    isDeferred: boolean
    minimumGuaranteedCreditLimit: number | null
}

export interface DeferMinimumGuaranteedCreditLimitRequest {
    customerId: string
}

export interface DeferMinimumGuaranteedCreditLimitResponse {
    isDeferred: boolean
    minimumGuaranteedCreditLimit: number | null
}

export interface GetMinimumGuaranteedCreditLimitResponse {
    isDeferred: boolean
    minimumGuaranteedCreditLimit: number | null
}

export interface ScoredExperimentMatchFactorRanking {
    name: 'experiment_useMatchFactorRanking'
    variant:
        | 'card_ranking_productrecommender'
        | 'card_ranking_hierarchical'
        | 'control'
}

export type ScoredExperiment = ScoredExperimentMatchFactorRanking
